import { I18n } from '@aws-amplify/core';
import { TIMELINE_SIDE } from '../types';
import { ONE_MILLISECOND } from '../utils/constants';
import { dayjsToSeconds } from '../utils/time-conversions';
import { isValidCycleDuration } from '../utils/helpers/cycle-helpers';
import type { CycleData, ValidationError, ValidationResult, CycleCallback, SideType } from '../types';

export class ValidationService {
	private static hasSameTypeId(cycleA: CycleData, cycleB: CycleData): boolean {
		if (!cycleA.type_id || !cycleB.type_id) return false;

		return cycleA.type_id === cycleB.type_id;
	}

	static validateCycleBounds(cycle: CycleData, videoDuration: number): ValidationError[] {
		const errors: ValidationError[] = [];

		if (cycle.start < 0) {
			errors.push({
				type: 'bounds',
				cycleId: cycle.id,
				message: I18n.get('Cycle start time cannot be negative'),
				severity: 'error'
			});
		}

		const exceedsVideo = cycle.end > videoDuration + ONE_MILLISECOND;

		if (exceedsVideo) {
			errors.push({
				type: 'video_duration_exceeded',
				cycleId: cycle.id,
				message: I18n.get('Final time exceeds video duration.'),
				severity: 'error'
			});
		}

		if (cycle.start > cycle.end) {
			errors.push({
				type: 'duration',
				cycleId: cycle.id,
				message: I18n.get('Cycle start time must be before end time'),
				severity: 'error'
			});
		}

		return errors;
	}

	static validateCycleDuration(_cycle: CycleData, _videoDuration: number): ValidationError[] {
		return [];
	}

	static detectOverlap(cycle1: CycleData, cycle2: CycleData): boolean {
		const cycle1Start = cycle1.start;
		const cycle1End = cycle1.end;

		const cycle2Start = cycle2.start;
		const cycle2End = cycle2.end;

		const overlaps = cycle1Start <= cycle2End && cycle2Start <= cycle1End;

		return overlaps;
	}

	static validateOverlaps(
		cycles: CycleData[],
		excludeIds: string[] = [],
		allowBilateral = true,
		allowOverlap = false
	): ValidationError[] {
		const errors: ValidationError[] = [];
		const activeCycles = cycles.filter((c) => !excludeIds.includes(c.id));

		activeCycles.forEach((currentCycle, index) => {
			const otherCycles = activeCycles.slice(index + 1);

			const overlappingCycle = otherCycles.find((otherCycle) => {
				const arePaired =
					currentCycle.paired_cycle_id === otherCycle.id && otherCycle.paired_cycle_id === currentCycle.id;

				if (allowBilateral && arePaired) {
					return false;
				}

				if (
					allowBilateral &&
					currentCycle.side &&
					otherCycle.side &&
					currentCycle.side !== otherCycle.side &&
					currentCycle.side !== TIMELINE_SIDE.BILATERAL &&
					otherCycle.side !== TIMELINE_SIDE.BILATERAL
				) {
					return false;
				}

				if (currentCycle.start === otherCycle.start && currentCycle.end === otherCycle.end) {
					if (!allowOverlap) {
						return true;
					}

					return this.hasSameTypeId(currentCycle, otherCycle);
				}

				const overlaps = this.detectOverlap(currentCycle, otherCycle);
				if (!overlaps) {
					return false;
				}

				if (!allowOverlap) {
					return true;
				}

				return this.hasSameTypeId(currentCycle, otherCycle);
			});

			if (overlappingCycle) {
				const isSameTypeConflict = this.hasSameTypeId(currentCycle, overlappingCycle);

				errors.push({
					type: 'overlap',
					cycleId: currentCycle.id,
					message: isSameTypeConflict
						? I18n.get('Overlap detected: two or more identical actions cannot occur in the same interval')
						: I18n.get('The start time cannot overlap another cycle. Please enter a value within the allowed range.'),
					conflictWith: overlappingCycle.id,
					severity: 'error'
				});
			}
		});

		return errors;
	}

	static validateCycle(cycle: CycleData, videoDuration: number): ValidationResult {
		const errors: ValidationError[] = [];

		errors.push(...this.validateCycleBounds(cycle, videoDuration));
		errors.push(...this.validateCycleDuration(cycle, videoDuration));

		return {
			isValid: errors.length === 0,
			errors
		};
	}

	static validateCycles(
		cycles: CycleData[],
		videoDuration: number,
		allowBilateral = true,
		allowOverlap = false
	): ValidationResult {
		const errors: ValidationError[] = [];

		cycles.forEach((cycle) => {
			errors.push(...this.validateCycleBounds(cycle, videoDuration));
			errors.push(...this.validateCycleDuration(cycle, videoDuration));
		});

		errors.push(...this.validateOverlaps(cycles, [], allowBilateral, allowOverlap));

		return {
			isValid: errors.length === 0,
			errors
		};
	}

	static validateCycleCallbacks(
		cycles: CycleCallback[],
		videoDuration: number,
		allowBilateral = true,
		allowOverlap = false
	): ValidationResult {
		const processedCycles = cycles.map((c) => ({
			id: c.id,
			name: c.name,
			type_id: c.type_id,
			start: dayjsToSeconds(c.start_time),
			end: dayjsToSeconds(c.end_time),
			identified: c.identified || false,
			side: c.side,
			paired_cycle_id: c.paired_cycle_id
		}));

		return this.validateCycles(processedCycles, videoDuration, allowBilateral, allowOverlap);
	}

	static canPositionCycle(
		cycleId: string,
		start: number,
		end: number,
		existingCycles: CycleData[],
		videoDuration: number,
		side?: SideType,
		allowBilateral = true,
		skipDurationCheck = false,
		_skipPairValidation = false,
		allowOverlap = false
	): { valid: boolean; error?: string } {
		if (!skipDurationCheck) {
			const duration = end - start;

			if (!isValidCycleDuration(duration, videoDuration)) {
				return {
					valid: false,
					error: I18n.get('Cycle duration must be at least 1s')
				};
			}
		}

		if (allowOverlap) {
			return { valid: true };
		}

		const otherCycles = existingCycles.filter((c) => c.id !== cycleId);
		const originalCycle = existingCycles.find((c) => c.id === cycleId);

		const cycleToValidate: CycleData = {
			id: cycleId,
			name: originalCycle?.name,
			type_id: originalCycle?.type_id,
			start,
			end,
			identified: false,
			side,
			paired_cycle_id: originalCycle?.paired_cycle_id
		};

		let cyclesToCheck = otherCycles;
		if (side && side !== TIMELINE_SIDE.BILATERAL) {
			cyclesToCheck = otherCycles.filter((cycle) => {
				if (allowBilateral && cycleToValidate.paired_cycle_id && cycle.id === cycleToValidate.paired_cycle_id) {
					return false;
				}

				return cycle.side === side || cycle.side === TIMELINE_SIDE.BILATERAL;
			});
		}

		const overlappingCycles = cyclesToCheck.filter((otherCycle) => {
			const overlaps = this.detectOverlap(cycleToValidate, otherCycle);

			return overlaps;
		});

		const hasSameTypeOverlap = overlappingCycles.some((otherCycle) => this.hasSameTypeId(cycleToValidate, otherCycle));

		if (overlappingCycles.length > 0 && (!allowOverlap || hasSameTypeOverlap)) {
			return {
				valid: false,
				error: hasSameTypeOverlap
					? I18n.get('Overlap detected: two or more identical actions cannot occur in the same interval')
					: I18n.get('The start time cannot overlap another cycle. Please enter a value within the allowed range.')
			};
		}

		if (cycleToValidate.paired_cycle_id && !_skipPairValidation) {
			const pairedCycle = existingCycles.find((c) => c.id === cycleToValidate.paired_cycle_id);

			if (pairedCycle && pairedCycle.side && pairedCycle.side !== side) {
				const pairedValidation = this.canPositionCycle(
					pairedCycle.id,
					start,
					end,
					existingCycles,
					videoDuration,
					pairedCycle.side,
					allowBilateral,
					skipDurationCheck,
					true
				);

				if (!pairedValidation.valid) {
					return {
						valid: false,
						error: I18n.get('No space available for bilateral pair')
					};
				}
			}
		}

		return { valid: true };
	}
}
